import cn from 'classnames';

import type { ReactNode } from 'react';
import type { ChangelogDiff, ChangelogRule, FormatterContext } from './types';

export type ChangelogDot = 'add' | 'delete' | 'edit' | 'other';

/** Builds the identifier matched by the rules regexes: `${kind}.${path.join('.')}` */
export function getStringIdentifier(change: ChangelogDiff): string {
  if (!change.path?.length) {
    return change.kind;
  }
  return `${change.kind}.${change.path.join('.')}`;
}

/** First rule whose regex matches the change identifier */
export function findRule(rules: ChangelogRule[], change: ChangelogDiff): ChangelogRule | undefined {
  const identifier = getStringIdentifier(change);
  return rules.find(rule => rule.regex.test(identifier));
}

function isPrimitive(value: unknown): value is boolean | number | string {
  return ['boolean', 'number', 'string'].includes(typeof value);
}

/**
 * Short readable label of a value: missing values become '-', objects their name (or id) and arrays
 * the labels of their items. Undefined when the value cannot be summarized.
 */
export function getValueLabel(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  if (isPrimitive(value)) {
    return String(value);
  }
  if (Array.isArray(value)) {
    const labels = value.map(getValueLabel);
    return labels.every(label => label !== undefined) ? labels.join(', ') || '-' : undefined;
  }
  const { id, name } = value as Record<string, unknown>;
  const label = name ?? id;
  return isPrimitive(label) ? String(label) : undefined;
}

/** Printable value: its label when it can be summarized, serialized otherwise */
export function formatValue(value: unknown): string {
  return getValueLabel(value) ?? JSON.stringify(value);
}

/** Readable value: its label, or a collapsed block with the full value when it cannot be summarized */
export function renderValue(value: unknown, { formatMessage }: FormatterContext): ReactNode {
  const label = getValueLabel(value);
  if (label !== undefined) {
    return label;
  }
  const summary = Array.isArray(value)
    ? formatMessage({ defaultMessage: '{count} items', id: 'changelogModal.message.items' }, { count: value.length })
    : formatMessage({ defaultMessage: 'details', id: 'changelogModal.message.details' });
  return (
    <details className="changelogModal__details">
      <summary>{summary}</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

export function getValClassName(dot: ChangelogDot): string {
  return cn('changelogModal__tableItemVal', `changelogModal__tableItemVal--${dot}Dot`);
}

/** Array changes are additions when the nested item has a right hand side value */
export function isItemAdded(change: ChangelogDiff): boolean {
  return Boolean(change.item?.rhs);
}

export function getAddedOrDeletedMessage(added: boolean, formatMessage: FormatterContext['formatMessage']): string {
  return added
    ? formatMessage({ defaultMessage: 'added', id: 'changelogModal.message.added' })
    : formatMessage({ defaultMessage: 'deleted', id: 'changelogModal.message.deleted' });
}

/** "<b>label</b> was edited from <b>lhs</b> to <b>rhs</b>" */
export function renderEdition(
  label: string,
  lhs: ReactNode,
  rhs: ReactNode,
  { formatMessage }: FormatterContext
): ReactNode {
  return (
    <div className={getValClassName('edit')}>
      <b>{label}</b>
      {` ${formatMessage({ defaultMessage: 'was edited from', id: 'changelogModal.message.edited' })} `}
      <b>{lhs}</b>
      {` ${formatMessage({ defaultMessage: 'to', id: 'changelogModal.message.to' })} `}
      <b>{rhs}</b>
    </div>
  );
}
